import { useAtomValue } from 'jotai';
import { ZonedDateTime } from '@internationalized/date';
import {
  histogramTimelineDragOffsetAtom,
  histogramVisibleTimeRangeAtom,
} from '../jotai/histogramAtoms';

type HistogramLocationIndicatorProps = {
  currentLocation?: ZonedDateTime;
  chartAreaRef: React.RefObject<HTMLDivElement>;
  isLoading?: boolean;
};

const HistogramLocationIndicator = ({
  currentLocation,
  chartAreaRef,
  isLoading,
}: HistogramLocationIndicatorProps) => {
  const [internalStartTime, internalEndTime] = useAtomValue(
    histogramVisibleTimeRangeAtom,
  );
  const timelineDragOffset = useAtomValue(histogramTimelineDragOffsetAtom);

  if (!currentLocation || !chartAreaRef.current || isLoading) {
    return null;
  }

  const chartArea = chartAreaRef.current;
  const rect = chartArea.getBoundingClientRect();
  const visibleDurationMs =
    internalEndTime.toDate().getTime() - internalStartTime.toDate().getTime();
  const pixelsPerMs = rect.width / visibleDurationMs;

  // Check if the current location is within the visible range (accounting for drag offset)
  const dragOffsetMs = -timelineDragOffset / pixelsPerMs;
  const actualVisibleStart = internalStartTime.add({
    milliseconds: dragOffsetMs,
  });
  const actualVisibleEnd = internalEndTime.add({
    milliseconds: dragOffsetMs,
  });

  if (
    currentLocation.compare(actualVisibleStart) < 0 ||
    currentLocation.compare(actualVisibleEnd) > 0
  ) {
    return null;
  }

  // Calculate position relative to the original viewport
  const lineTimeMs =
    currentLocation.toDate().getTime() - internalStartTime.toDate().getTime();
  const newPosition = lineTimeMs * pixelsPerMs + timelineDragOffset;

  return (
    <div
      className={`pointer-events-none absolute z-histogramVerticalLine bg-blue-600 ${
        timelineDragOffset === 0 ? 'transition-all duration-300 ease-out' : ''
      }`}
      style={{
        left: `${newPosition}px`,
        top: '-4px',
        bottom: '-4px',
        width: '2px',
      }}
      data-testid="histogram-vertical-line"
    />
  );
};

export { HistogramLocationIndicator };
